import { ApolloCache } from '@apollo/client';
import type { MockedResponse } from '@apollo/client/testing';
import { InitializeApolloPersisted } from 'apolloGraphql/initialize-apollo-persisted';
import { MML_APOLLO_CACHE } from 'apolloGraphql/mml-apollo-cache';
import { CLIENT_SCHEMA } from 'apolloGraphql/schemas/client-schema';
import PropTypes from 'prop-types';
import { FC, PropsWithChildren, useMemo } from 'react';
import { AdsConfig, AppConfig, BillboardsConfig } from 'types/configs';
import { APP_VERSION } from 'version';
import { APP_NAME } from '../app-constants';
import useInitialScripts from '../hooks/useInitialScripts';
import { AppContext } from './app-context';
import { useContextSetup } from './useContextSetup';
import { useDatadogGlobals } from '../hooks/useDatadogGlobal';

export type ContextProps = PropsWithChildren<{
  adsConfig?: AdsConfig | null | undefined;
  appConfig?: AppConfig | null | undefined;
  billboardsConfig?: BillboardsConfig | null | undefined;
  cache?: ApolloCache<Record<string, unknown>>;
  mocks?: MockedResponse[];
}>;

export const Context: FC<ContextProps> = (props) => {
  useInitialScripts();
  const {
    adsConfig,
    appConfig,
    appConfigUrl,
    appControlsFetchCounter,
    appControlsPollingEnabled,
    billboardsConfig,
    boxscoreFetchCounter,
    boxscorePollingEnabled,
    currentFetchCounter,
    currentPollingEnabled,
    device,
    setAdsConfig,
    setAppConfig,
    setAppConfigUrl,
    setAppControlsFetchCounter,
    setAppControlsPollingEnabled,
    setBillboardsConfig,
    setBoxscoreFetchCounter,
    setBoxscorePollingEnabled,
    setCurrentFetchCounter,
    setCurrentPollingEnabled,
    setWebOSDeviceInfo,
    webosDeviceInfo,
    captionsData,
  } = useContextSetup();

  useDatadogGlobals(appConfig);

  const contextValue = useMemo(
    () => ({
      device,
      appConfigUrl,
      appConfig,
      adsConfig,
      billboardsConfig,
      captionsData,
      webosDeviceInfo,
      currentPollingEnabled,
      boxscorePollingEnabled,
      appControlsPollingEnabled,
      currentFetchCounter,
      boxscoreFetchCounter,
      appControlsFetchCounter,
      incrementCurrentFetchCounter: () => setCurrentFetchCounter((current) => ++current),
      incrementBoxscoreFetchCounter: () => setBoxscoreFetchCounter((current) => ++current),
      incrementAppControlsFetchCounter: () => setAppControlsFetchCounter((current) => ++current),
      setCurrentPollingEnabled,
      setBoxscorePollingEnabled,
      setAppControlsPollingEnabled,
      setAppConfigUrl,
      setAppConfig,
      setAdsConfig,
      setBillboardsConfig,
      setWebOSDeviceInfo,
    }),
    [
      device,
      appConfigUrl,
      appConfig,
      adsConfig,
      billboardsConfig,
      captionsData,
      webosDeviceInfo,
      currentPollingEnabled,
      boxscorePollingEnabled,
      appControlsPollingEnabled,
      currentFetchCounter,
      boxscoreFetchCounter,
      appControlsFetchCounter,
      setCurrentFetchCounter,
      setBoxscoreFetchCounter,
      setAppControlsFetchCounter,
      setCurrentPollingEnabled,
      setBoxscorePollingEnabled,
      setAppControlsPollingEnabled,
      setAppConfigUrl,
      setAppConfig,
      setAdsConfig,
      setBillboardsConfig,
      setWebOSDeviceInfo,
    ],
  );

  return (
    <AppContext.Provider value={contextValue}>
      <InitializeApolloPersisted
        uri={appConfig?.gql.host || ''}
        appVersion={APP_VERSION.version}
        appName={APP_NAME}
        cache={MML_APOLLO_CACHE}
        typeDefs={CLIENT_SCHEMA}
      >
        {props.children}
      </InitializeApolloPersisted>
    </AppContext.Provider>
  );
};

Context.propTypes = {
  children: PropTypes.node.isRequired,
};
